import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';

export const ASSET_NAME = 'landing-page';

export const createPopularListingsParams = (config, categoryLevel1) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return {
    page: 1,
    perPage: 100,
    include: ['author', 'images'],
    sort: '-meta_averageRating',
    ...(categoryLevel1 ? { pub_categoryLevel1: categoryLevel1 } : {}),
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'deleted',
      'state',
      'metadata',
      'publicData.listingType',
      'publicData.category',
      'publicData.categoryLevel1',
      'publicData.categoryLevel2',
      'publicData.categoryLevel3',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.cardStyle',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.priceVariationsEnabled',
      'publicData.priceVariants',
      'publicData.sessionPricing',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };
};

// ================ Async Thunks ================ //

export const fetchPopularListings = createAsyncThunk(
  'LandingPage/fetchPopularListings',
  async ({ searchParams, config }, { dispatch, extra: sdk }) => {
    const allListings = [];
    const MAX_PAGES = 3;
    let currentPage = 1;
    let totalPages = 1;

    try {
      while (currentPage <= MAX_PAGES && currentPage <= totalPages) {
        const response = await sdk.listings.query({
          ...searchParams,
          page: currentPage,
        });

        const listings = response.data.data;
        allListings.push(...listings);

        totalPages = response.data.meta.totalPages;

        // Dispatch entities from each page to the store
        const listingFields = config?.listing?.listingFields;
        const sanitizeConfig = { listingFields };
        dispatch(addMarketplaceEntities(response, sanitizeConfig));

        currentPage++;
      }

      return { data: { data: allListings } };
    } catch (e) {
      throw storableError(e);
    }
  }
);

// ================ Slice ================ //

const landingPageSlice = createSlice({
  name: 'LandingPage',
  initialState: {
    popularListingsIds: [],
    currentPopularListingsRequestId: null,
    fetchPopularListingsInProgress: false,
    fetchPopularListingsError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchPopularListings.pending, (state, action) => {
        state.currentPopularListingsRequestId = action.meta.requestId;
        state.fetchPopularListingsInProgress = true;
        state.fetchPopularListingsError = null;
      })
      .addCase(fetchPopularListings.fulfilled, (state, action) => {
        const { requestId } = action.meta || {};
        if (
          state.currentPopularListingsRequestId &&
          requestId &&
          state.currentPopularListingsRequestId !== requestId
        ) {
          return;
        }

        const listings = action.payload.data.data || [];
        // client-side sorting as a fallback/guarantee
        const sortedListings = [...listings].sort((a, b) => {
          const ratingA = a.attributes?.metadata?.averageRating || 0;
          const ratingB = b.attributes?.metadata?.averageRating || 0;
          return ratingB - ratingA;
        });

        state.popularListingsIds = sortedListings
          .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
          .map(l => l.id);
        state.currentPopularListingsRequestId = null;
        state.fetchPopularListingsInProgress = false;
      })
      .addCase(fetchPopularListings.rejected, (state, action) => {
        const { requestId } = action.meta || {};
        if (
          state.currentPopularListingsRequestId &&
          requestId &&
          state.currentPopularListingsRequestId !== requestId
        ) {
          return;
        }

        state.currentPopularListingsRequestId = null;
        state.fetchPopularListingsInProgress = false;
        state.fetchPopularListingsError = action.payload;
      });
  },
});

export default landingPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const pageAsset = { landingPage: `content/pages/${ASSET_NAME}.json` };
  const listingsParams = createPopularListingsParams(config);

  const popularListingsPromise = dispatch(fetchPopularListings({
    searchParams: listingsParams,
    config,
  }));

  const pageAssetsPromise = dispatch(fetchPageAssets(pageAsset, true));

  return Promise.all([popularListingsPromise, pageAssetsPromise]);
};
